import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useCuentaAtlas } from '../hooks/useCuentaAtlas';
import {
  aceptarInvitacion,
  EquipoError,
  rutaUnirme,
  verInvitacion,
  type InvitacionPublica,
} from '../lib/equipoApi';
import { guardarInvitacionPendiente, olvidarInvitacionPendiente } from '../lib/invitacionPendiente';
import { estadoDeInvitacion } from './unirme/estadoDeInvitacion';
import { CACHE_KEYS, invalidateCache } from '../utils/sessionCache';
import { AUTH_PATH } from '../components/onboarding/onboardingV2Routes';
import { LogoDeMarca } from '../branding';

/**
 * `/unirme/:token` — la puerta de entrada de un invitado
 * (spec docs/superpowers/specs/2026-09-11-invitaciones-equipo-design.md §3).
 *
 * Pública: el invitado puede no tener cuenta. Los estados los decide
 * `estadoDeInvitacion` (pura, testeada); acá sólo se dibujan y se dispara el canje.
 */

/** Lo que el front cacheó de la cuenta y que después del canje miente (spec §3.2). */
function limpiarCacheDeCuenta() {
  invalidateCache(CACHE_KEYS.USER_PROFILE);
  invalidateCache(CACHE_KEYS.COMPANY_DATA);
  try {
    localStorage.removeItem('atlas-user-plan');
    localStorage.removeItem('atlas-user-group');
  } catch {
    /* noop */
  }
  olvidarInvitacionPendiente();
}

const Boton = ({
  children,
  onClick,
  secundario = false,
  disabled = false,
}: {
  children: ReactNode;
  onClick: () => void;
  secundario?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full py-2.5 px-5 rounded-lg text-[15px] font-medium transition-colors disabled:opacity-60 ${
      secundario
        ? 'bg-white border border-black/[0.08] text-brand-black hover:bg-black/[0.02]'
        : 'bg-brand-primary text-white hover:opacity-90'
    }`}
  >
    {children}
  </button>
);

export const UnirmeAEquipo = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const cuenta = useCuentaAtlas();
  const [invitacion, setInvitacion] = useState<InvitacionPublica | null>(null);
  const [haySesion, setHaySesion] = useState<boolean | null>(null);
  const [errorRed, setErrorRed] = useState<string | null>(null);
  const [uniendo, setUniendo] = useState(false);
  const [listo, setListo] = useState<string | null>(null);

  useEffect(() => {
    if (token) guardarInvitacionPendiente(token);
    let activo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (activo) setHaySesion(!!data.session);
    });
    verInvitacion(token)
      .then((inv) => {
        if (activo) setInvitacion(inv);
      })
      .catch(() => {
        if (activo) setErrorRed('No pudimos conectarte. Probá de nuevo.');
      });
    return () => {
      activo = false;
    };
  }, [token]);

  const estado = estadoDeInvitacion({
    invitacion,
    haySesion,
    cargandoCuenta: cuenta.cargando,
    idCompanyActual: cuenta.idCompany,
    planActual: cuenta.plan,
  });

  useEffect(() => {
    if (estado.tipo === 'ya_es_miembro') {
      olvidarInvitacionPendiente();
      navigate('/', { replace: true });
    }
  }, [estado.tipo, navigate]);

  const unirme = async () => {
    setUniendo(true);
    setErrorRed(null);
    try {
      const r = await aceptarInvitacion(token);
      limpiarCacheDeCuenta();
      setListo(r.nombreMarca);
      // `assign` y no `navigate`: ProtectedRoute tiene que volver a correr checkUserProfile
      // desde cero, como después del alta.
      window.setTimeout(() => window.location.assign('/'), 1500);
    } catch (e) {
      if (e instanceof EquipoError && (e.tipo === 'equipo_lleno' || e.tipo === 'plan_sin_equipo')) {
        setInvitacion({ valido: false, motivo: e.tipo });
      } else if (
        e instanceof EquipoError &&
        (e.tipo === 'invitacion_invalida' || e.tipo === 'invitacion_vencida')
      ) {
        setInvitacion({ valido: false, motivo: 'vencido' });
      } else if (e instanceof EquipoError && e.tipo === 'dueno_con_equipo') {
        // El backend no lo deja mudarse mientras sea dueño de una cuenta con gente: su
        // equipo quedaría sin nadie que lo administre. El motivo ya viene redactado.
        setErrorRed(e.message);
      } else {
        setErrorRed('No pudimos conectarte. Probá de nuevo.');
      }
    } finally {
      setUniendo(false);
    }
  };

  const irAAuth = (modo: 'signup' | 'login') => {
    const next = encodeURIComponent(rutaUnirme(token));
    navigate(modo === 'signup' ? `${AUTH_PATH}?mode=signup&next=${next}` : `${AUTH_PATH}?next=${next}`);
  };

  let cuerpo: ReactNode;
  if (listo) {
    cuerpo = (
      <>
        <h1 className="text-[22px] font-semibold text-brand-black">Ya sos parte de {listo}</h1>
        <p className="text-[15px] text-neutral-500 mt-2">Te llevamos a la app…</p>
      </>
    );
  } else if (errorRed && !invitacion) {
    cuerpo = (
      <>
        <h1 className="text-[22px] font-semibold text-brand-black">No pudimos abrir la invitación</h1>
        <p className="text-[15px] text-neutral-500 mt-2">{errorRed}</p>
        <div className="mt-6">
          <Boton onClick={() => window.location.reload()}>Probar de nuevo</Boton>
        </div>
      </>
    );
  } else if (estado.tipo === 'cargando' || estado.tipo === 'ya_es_miembro') {
    cuerpo = <Loader2 className="w-6 h-6 animate-spin text-brand-primary mx-auto" />;
  } else if (estado.tipo === 'invalida') {
    cuerpo = (
      <>
        <h1 className="text-[22px] font-semibold text-brand-black">
          {estado.motivo === 'equipo_lleno'
            ? 'Este equipo ya está completo'
            : estado.motivo === 'plan_sin_equipo'
              ? 'Este equipo no está sumando personas'
              : 'Este link ya no sirve'}
        </h1>
        <p className="text-[15px] text-neutral-500 mt-2">
          {estado.motivo === 'equipo_lleno'
            ? 'El equipo ya tiene todas las personas que incluye su plan. Pedile a quien te invitó que haga lugar.'
            : estado.motivo === 'plan_sin_equipo'
              ? 'El plan de esa cuenta no incluye sumar personas. Pedile a quien te invitó que lo revise.'
              : 'Pedile uno nuevo a quien te invitó.'}
        </p>
        <div className="mt-6">
          {/* Olvidar el token ANTES de salir: `checkUserProfile` manda a `/unirme/<token>`
              a todo el que no tenga empresa y tenga uno guardado (spec §3.3), así que sin
              esto "Ir a Atlas" rebota de vuelta a esta misma pantalla muerta y el invitado
              no llega nunca al onboarding. Igual que el Cancelar de abajo. */}
          <Boton
            secundario
            onClick={() => {
              olvidarInvitacionPendiente();
              navigate('/');
            }}
          >
            Ir a Atlas
          </Boton>
        </div>
      </>
    );
  } else {
    const titulo = (
      <h1 className="text-[22px] font-semibold text-brand-black">
        Te invitaron a <span className="text-brand-primary">{estado.nombreMarca}</span>
      </h1>
    );
    if (estado.tipo === 'sin_sesion') {
      cuerpo = (
        <>
          {titulo}
          <p className="text-[15px] text-neutral-500 mt-2">
            Creá tu cuenta o entrá con la que ya tenés para sumarte al equipo.
          </p>
          <div className="mt-6 space-y-2">
            <Boton onClick={() => irAAuth('signup')}>Crear cuenta</Boton>
            <Boton secundario onClick={() => irAAuth('login')}>Ya tengo cuenta</Boton>
          </div>
        </>
      );
    } else if (estado.tipo === 'sin_empresa') {
      cuerpo = (
        <>
          {titulo}
          <p className="text-[15px] text-neutral-500 mt-2">
            Vas a poder trabajar con los productos, las marcas y los créditos de esta cuenta.
          </p>
          {errorRed && <p className="text-[13px] text-red-600 mt-3">{errorRed}</p>}
          <div className="mt-6">
            <Boton onClick={unirme} disabled={uniendo}>{uniendo ? 'Entrando…' : 'Unirme'}</Boton>
          </div>
        </>
      );
    } else {
      cuerpo = (
        <>
          {titulo}
          {estado.planActualPago && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[14px] text-amber-900 text-left">
              Tu cuenta actual tiene un plan pago. Si te unís, ese plan queda en la otra cuenta.
            </div>
          )}
          <p className="text-[15px] text-neutral-500 mt-3">
            Si entrás, dejás tu cuenta actual. Lo que tenés ahí no se borra.
          </p>
          {errorRed && <p className="text-[13px] text-red-600 mt-3">{errorRed}</p>}
          <div className="mt-6 space-y-2">
            <Boton onClick={unirme} disabled={uniendo}>{uniendo ? 'Entrando…' : 'Unirme igual'}</Boton>
            <Boton
              secundario
              onClick={() => {
                olvidarInvitacionPendiente();
                navigate('/');
              }}
            >
              Cancelar
            </Boton>
          </div>
        </>
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Un solo elemento: el lockup ya trae isotipo + palabra, así que no va `gap` ni
            un `<span>` al lado. `h-8` sobre los 1967×266 del SVG da ~236px de ancho, algo
            más de la mitad de la tarjeta (448px) — proporción de marca, no de cromo (la
            sidebar lo usa a 19px porque ahí es cromo). */}
        {/* El lockup horizontal (isotipo + marca) — el MISMO que la sidebar del shell arriba a
            la izquierda, y el del branding del cliente si tiene uno. Es un asset local, que en
            una pantalla PÚBLICA importa: el logo viejo salía a buscarse a Supabase. */}
        <div className="flex justify-center mb-8">
          <LogoDeMarca
            variante="horizontal"
            draggable={false}
            className="h-8 w-auto select-none"
          />
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-black/[0.06] p-8 text-center">
          <Users className="w-10 h-10 text-brand-primary mx-auto mb-4" />
          {cuerpo}
        </div>
      </div>
    </div>
  );
};

export default UnirmeAEquipo;
